import type { StorefrontSettings } from "@workspace/api-client-react";
import { BRANDS } from "./brands";
import { PRICING_CONFIG } from "./pricing";

const DEFAULT_HOMEPAGE_HERO_ENABLED_BRANDS = ["ethnc", "qalamkar", "beechtree"];
const DEFAULT_HOMEPAGE_HERO_BRAND_ORDER = [
  ...DEFAULT_HOMEPAGE_HERO_ENABLED_BRANDS,
  ...BRANDS.map(brand => brand.slug).filter(slug => !DEFAULT_HOMEPAGE_HERO_ENABLED_BRANDS.includes(slug)),
];

export const DEFAULT_STOREFRONT_SETTINGS: StorefrontSettings = {
  pricing: {
    pkrToBdtDivisor: PRICING_CONFIG.pkrToBdtDivisor,
    roundToBDT: PRICING_CONFIG.roundToBDT,
    aboveLastTierMarkupRate: PRICING_CONFIG.aboveLastTierMarkupRate,
    tiers: PRICING_CONFIG.tiers.map(tier => ({ ...tier })),
  },
  brands: BRANDS.map(brand => ({ ...brand })),
  brandOrder: BRANDS.map(brand => ({ slug: brand.slug, visible: true })),
  shopByBrandRotationEnabled: true,
  shopByBrandRotationIntervalSeconds: 6,
  heroShopBrandSlugs: ["ethnc"],
  homepageHero: {
    enabled: true,
    intervalSeconds: 6,
    brands: DEFAULT_HOMEPAGE_HERO_BRAND_ORDER.map(slug => ({
      slug,
      enabled: DEFAULT_HOMEPAGE_HERO_ENABLED_BRANDS.includes(slug),
      overrideProductId: null,
    })),
  },
  brandMedia: (["ethnc", "lulusar", "khaadi", "sapphire", "beechtree", "qalamkar", "studiosy", "vanya"] as const)
    .map(slug => ({ slug, logoUrl: null, featuredImageUrl: null, bannerImageUrl: null })),
  imageFocalPoints: [],
  homepageSections: [],
  shopByBrandCopy: {
    eyebrow: "FIND YOUR LABEL",
    heading: "Shop by brand",
    subheading: "",
    ctaText: "VIEW ALL {brand}",
    hiddenCopyFields: [],
    sectionManaged: false,
    },
  socialLinks: {
    facebook: { enabled: true, url: "https://www.facebook.com/chamakpattian" },
    instagram: { enabled: true, url: "https://www.instagram.com/chamakpattian/" },
    whatsapp: { enabled: true, number: "923391660662" },
  },
  paymentMethods: [
    { key: "bkash", label: "bKash", details: "", enabled: true },
    { key: "nagad", label: "Nagad", details: "", enabled: true },
    { key: "rocket", label: "Rocket", details: "", enabled: true },
    { key: "bank_transfer", label: "Bank transfer", details: "", enabled: true },
  ],
  whatsappNumber: "",
  whatsappScreenshotNumber: "+92 339 166 0662",
  whatsappScreenshotMessage: "Hi ChamakPatti, my order {orderNumber} for {total} BDT. Paid by {method} to {account}. Sending my payment screenshot now.",
  whatsappScreenshotButtonLabel: "Send payment screenshot on WhatsApp",
  whatsappScreenshotHelperText: "Tap the + in WhatsApp to attach your screenshot, then send.",
  saleEnabled: true,
  showSoldOutProductsInLists: false,
  minSaleDiscountPercent: 1,
  deliveryTimeline: "Estimated delivery to Dhaka: about 14 to 20 days from your order date. This includes about 3 to 5 days for the brand's parcel to reach our Karachi office, followed by shipping and processing to Dhaka. These are estimates, not guarantees.",
  deliveryBagMessage: "Delivery differs by piece. Pieces marked 'Delivery included' already cover an estimate. For other pieces, delivery is charged by weight when your order arrives, and several pieces may add to it.",
  deliveryNoteEnabled: true,
  deliveryTextA: "Delivery to Dhaka is included as an estimate for a parcel up to {included_weight_kg} kg. If your parcel weighs more, {rate_per_gram} BDT per gram applies to the extra weight, payable on delivery.",
  deliveryTextB: "Delivery is estimated at {included_weight_kg} kg. If your parcel weighs more, {rate_per_gram} BDT per gram applies to the extra weight, payable on delivery.",
  deliveryWithoutPieceCountMode: "estimate",
  deliveryNotIncludedText: "Delivery is not included. It is charged on delivery at {rate_per_gram} BDT per gram, based on your parcel's weight.",
  shippingRatePerKgBDT: 1750,
  pieceCountWeightsKg: { "1": null, "2": null, "3": null },
  defaultDeliveryWeightKg: null,
  seoOverrides: {
    home: { title: "", description: "" },
    brands: { title: "", description: "" },
    sale: { title: "", description: "" },
    shop: { title: "", description: "" },
    terms: { title: "", description: "" },
  },
  content: {
    announcementText: "PAKISTANI DESIGN. A LITTLE CLOSER TO HOME.",
    heroEyebrow: "FROM PAKISTAN, WITH SPIRIT",
    heroHeading: "A Riot\nof Color.",
    heroSubheading: "Expressive Pakistani labels, brought to Bangladesh. Find the pieces that feel like you.",
    heroCtaText: "SHOP ETHNC",
    heroBottomNote: "CURATED FOR YOUR EVERYDAY",
    heroImageEyebrow: "THE ETHNC EDIT · 01",
    heroImageCaption: "Made to be noticed.",
    heroImageUrl: null,
    featuredEyebrow: "JUST LANDED IN YOUR CORNER",
    featuredHeading: "Featured styles",
    emptyCatalogMessage: "All of these beauties found a home before you got here. New arrivals are on their way. Peek at our other collections while you wait.",
    saleEyebrow: "A LITTLE SOMETHING OFF",
    saleHeading: "The Sale edit",
    shopAllEyebrow: "THE FULL COLLECTION",
    shopAllTagline: "A considered edit of Pakistani labels, all in one place.",
    storyEyebrow: "A NOTE FROM DHAKA",
    storyHeading: "Good clothes.\nFar fewer miles.",
    storyBody: "Chamakpatti brings authentic Pakistani labels a little closer. Explore expressive prints, thoughtful details, and clothes made for real life.",
    footerHeading: "A little color goes a long way.",
    footerBody: "Pakistani fashion, thoughtfully brought to Bangladesh. The pieces you love, from the labels you know.",
    footerPromiseText: "Real pieces from the labels you love.\nClear pricing in BDT.\nA little riot, delivered.",
    footerTagline: "A RIOT OF COLOR.",
    footerCopyrightLabel: "CHAMAKPATTI · KARACHI, PAKISTAN",
    logoUrl: null,
    footerLogoUrl: null,
    faviconUrl: null,
    homepageShareImageUrl: null,
    termsTitle: "Terms of service",
    termsIntro: "Read information about orders, payments, prices, delivery, product availability, returns, and privacy below.",
    termsSections: [
      { heading: "Orders and order requests", body: "Submitting an order creates a saved order request. Check the confirmation details and contact Chamakpatti if you need help with your request." },
      { heading: "Payment and confirmation", body: "Available payment methods and their instructions are shown during checkout. Follow the instructions for the method you selected and keep your payment reference." },
      { heading: "Pricing and currency", body: "Store prices are displayed in Bangladeshi taka (BDT). The checkout summary shows the amounts used for your order." },
      { heading: "Shipping and delivery estimates", body: "The delivery timeline shown on the site is an estimate, not a guaranteed arrival date. Contact Chamakpatti if you need an update about an order." },
      { heading: "Product information and availability", body: "Product details, colors, and images are provided to help you choose. Availability can change, so an item may no longer be available when an order is reviewed." },
      { heading: "Changes, cancellations, returns, and refunds", body: "Contact Chamakpatti as soon as possible if you need to change or cancel an order. Confirm the current return, exchange, and refund rules with the store before making a purchase." },
      { heading: "Privacy and contact details", body: "Checkout uses the name, phone number, and delivery address you provide to process and follow up on your order. Contact the store if any details need correction." },
    ],
  },
};